import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Building,
  Play,
  Eye,
  FileText,
  Download,
  Search,
  VideoOff,
} from "lucide-react";
import type { SearchData, Session } from "../types";
import { INITIAL_SESSION_SEARCH_DATA } from "../constants/searchSessions";
import type { ProceedingLogs } from "../types/searchSessions";
import { toast } from "react-toastify";
import { CreateArchival, getVideoV2 } from "../api/archivalReqestApis";
import { useAuth } from "../context/AuthProvider";
import logger from "../utils/logger";
import api from "../lib/api";
import { formatDisplayDate } from "../utils/Date";
import Pagination from "../utils/Pagination";

interface SessionResultsProps {
  results: Session[];
  proceedingData?: ProceedingLogs;
  isLoading: boolean;
  setResults: React.Dispatch<React.SetStateAction<Session[]>>;
  setSearchData: React.Dispatch<React.SetStateAction<SearchData>>;
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  searchMode: string;
  hasSearched: boolean;
}

const SessionResults: React.FC<SessionResultsProps> = ({
  results,
  proceedingData,
  isLoading,
  setResults,
  setSearchData,
  page,
  totalPages,
  onPageChange,
  searchMode,
  hasSearched,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showReqModal, setShowReqModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any | null>(null);
  const [playingCnr, setPlayingCnr] = useState<string | null>(null); // loading state per row

  // ── Archival request ──────────────────────────────────────────────────────
  const handleRequest = async (formdata: any) => {
    try {
      const payload = {
        cnr: formdata?.cnr,
        caseId: formdata?._id,
        hearingDate: formdata?.hearingDate,
        proceedingLogId: proceedingData?._id,
      };

      setLoading(true);
      const res = await CreateArchival(payload);
      const data = res?.data;

      if (data?.status) {
        toast.success(data?.message || "Archival requested successfully");
        setShowReqModal(false);
        setResult(null);
        setResults([]);
        setSearchData(INITIAL_SESSION_SEARCH_DATA);
      } else {
        toast.error(data?.message || "Failed to add case");
      }
    } catch (error: any) {
      toast.error(error.message, { toastId: "request-failed" });
    } finally {
      setLoading(false);
    }
  };

  // ── Live stream navigate ──────────────────────────────────────────────────
  const handleNavigate = (result: any) => {
    navigate("/stream", {
      state: { cnr: result.cnr, date: result.hearingDate },
    });
  };

  const handleWatchNavigate = (result: any) => {
    const room = result?.courtRoomNumber || result?.benchCourtRoomNumber;
    if (room) {
      navigate(`/watch-live/${room}`);
    } else {
      toast.error("Courtroom not available");
    }
  };

  // ── Play recorded video ───────────────────────────────────────────────────
  const handlePlayVideo = async (cnr: string, date: string) => {
    try {
      setPlayingCnr(cnr);

      // Step 1: validate — confirms file exists and is available
      // const res = await getVideo({ cnr, hearingDate: date });
      // console.log("rees....",res.data);
      // if (!res.data?.status) {
      //   toast.error(res.data?.message || "Failed to play video");
      //   return;
      // }

      // Step 2: build stream URL — GET endpoint, browser sends range headers natively
      const payload = { cnr, hearingDate: date };
      const res = await getVideoV2(payload);

      if (!res.data?.status) {
        toast.error(res.data?.message || "Failed to play video");
        return;
      }
      const streamKey = import.meta.env.VITE_APP_STREAM_KEY; // shared secret, mirrors backend STREAM_SECRET
      const params = new URLSearchParams({ cnr, hearingDate: date });
      if (streamKey) params.set("key", streamKey);

      const streamUrl = res.data.data.tokenUrl;

      navigate(
        `/recording?${new URLSearchParams({ cnr, hearingDate: date })}`,
        {
          state: {
            videoUrl: streamUrl,
            user,
            cnr,
            hearingDate: date,
            type: "Recorded Video",
          },
        },
      );
    } catch (error: any) {
      logger.error("[handlePlayVideo] error:", error);
      toast.error(error.message || "Failed to play video");
    } finally {
      setPlayingCnr(null);
    }
  };

  // ── Status badge ──────────────────────────────────────────────────────────
  const getStatusBadge = (status: Session["status"]) => {
    logger.log("status", status);
    const base = "px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap";
    switch (status) {
      case "live":
        return (
          <span className={`${base} bg-red-100 text-red-800`}>🟢 Live</span>
        );
      case "available":
        return (
          <span className={`${base} bg-blue-100 text-blue-800`}>
            📹 Available
          </span>
        );
      case "archived":
        return (
          <span className={`${base} bg-gray-100 text-gray-800`}>
            📁 Archived
          </span>
        );
      case "rec":
        return (
          <span className={`${base} bg-red-100 text-red-800`}>
            🔴 No Live Available
          </span>
        );
      case "paused":
        return (
          <span className={`${base} bg-red-100 text-red-800`}>🟢 Live</span>
        );
      case "processing":
        return (
          <span className={`${base} bg-yellow-100 text-yellow-800`}>
            🟡 Processing
          </span>
        );
      default:
        return null;
    }
  };

  // ── Action button per status ──────────────────────────────────────────────
  // Approved objection: hearing and recording run, live streaming does not
  const STREAMING_RESTRICTED_NOTE =
    "Live streaming is not allowed for this hearing due to an approved objection.";

  const renderStatusBadge = (result: Session) => {
    if (!result.streamingRestricted) {
      return getStatusBadge(result.streamingStatus);
    }
    return (
      <span
        title={STREAMING_RESTRICTED_NOTE}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap bg-amber-100 text-amber-800"
      >
        <VideoOff size={14} aria-hidden="true" />
        Streaming not allowed
      </span>
    );
  };

  const renderStatusButton = (result: Session) => {
    const status = result.streamingStatus;
    // no Watch Live; recordings stay viewable / requestable
    if (
      result.streamingRestricted &&
      status !== "available" &&
      status !== "archived"
    ) {
      return (
        <span className="text-xs text-gray-500">Objection approved</span>
      );
    }
    return getStatusButton(status, result);
  };

  const getStatusButton = (status: Session["status"], result: any) => {
    switch (status) {
      case "live":
        return (
          <button
            className="inline-flex whitespace-nowrap items-center gap-2 bg-red-600 hover:bg-red-700
             text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            onClick={() => handleNavigate(result)}
          >
            <Play size={13} />
            Watch Live
          </button>
        );

      case "paused":
        return (
          <button
            className="inline-flex whitespace-nowrap items-center gap-2 bg-red-600 hover:bg-red-700
             text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors"
            onClick={() => handleNavigate(result)}
          >
            <Play size={13} />
            Watch Live
          </button>
        );

      case "available":
        return (
          <div className="flex gap-1.5">
            {/* Download — SuperAdmin and Judge only */}
            {user?.role &&
              ["SuperAdmin", "Judge"].includes(user.role) &&
              result?.proceeding?._id && (
                <button
                  className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-1.5 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                  onClick={() => {
                    const url = `${api.defaults.baseURL}/api/search/download-video/${result.proceeding._id}`;
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute(
                      "download",
                      `${result.proceeding.name}.mp4`,
                    );
                    link.click();
                  }}
                >
                  <Download size={16} />
                  Download
                </button>
              )}

            {/* View Recording */}
            <button
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-1.5 py-1.5 rounded-lg transition-colors whitespace-nowrap disabled:opacity-60"
              onClick={() => handlePlayVideo(result.cnr, result.hearingDate)}
              disabled={playingCnr === result.cnr}
            >
              {playingCnr === result.cnr ? (
                <>
                  <span className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Eye size={13} />
                  View Recording
                </>
              )}
            </button>
          </div>
        );

      case "archived":
        return (
          <button
            onClick={() => {
              setShowReqModal(true);
              setResult(result);
            }}
            className="inline-flex items-center gap-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
          >
            <FileText size={13} />
            Request Access
          </button>
        );

      default:
        return null;
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="mt-6 py-16 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-gray-600">Searching for sessions...</p>
      </div>
    );
  }

  // ── Empty states ──────────────────────────────────────────────────────────
  if (!hasSearched && results.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-sm text-gray-500">
        <div className="flex flex-col items-center justify-center space-y-3">
          <Search className="h-12 w-12 text-gray-300" />
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-0">
          Search session
        </h3>
        <p className="text-gray-600">
          Enter the required details above and search to view courtroom
          sessions.
        </p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="px-6 py-16 text-center text-sm text-gray-500">
        <div className="flex flex-col items-center justify-center space-y-3">
          <FileText className="h-12 w-12 text-gray-300" />
        </div>
        <h3 className="text-lg font-medium text-gray-600 mb-0">
          No session found
        </h3>
        <p className="text-gray-600">
          No sessions found for the selected criteria. Try adjusting your search
          parameters.
        </p>
      </div>
    );
  }

  const liveResult = results.find((r) => r.streamingStatus === "live");
  const liveCount = results.filter((r) => r.streamingStatus === "live").length;

  return (
    <>
      <div className="mt-6">
        <div className="flex items-center justify-between px-1 mb-5">
          <h3 className="text-xl font-semibold text-gray-900 mb-4 px-4">
            {results.length === 1
              ? "1 Search Result"
              : `Search Results ${results.length}`}
          </h3>

          {liveCount > 0 && searchMode !== "cnr" && liveResult && (
            <button
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition-colors shadow-sm"
              onClick={() => handleWatchNavigate(liveResult)}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Court Hall Watch Live
            </button>
          )}
        </div>

        {/* ── Desktop Table ── */}
        <div className="hidden md:block overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-600 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Case Number / CNR</th>
                <th className="px-4 py-3">Petitioner</th>
                <th className="px-4 py-3">Respondent</th>
                <th className="px-8 py-3 text-start">Status</th>
                <th className="px-4 py-3 text-start">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {results.map((result, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-600">
                    {formatDisplayDate(result?.hearingDate)}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-xs text-gray-500">{result?.cnr}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-[120px]">
                    <p className="break-words">
                      {(result as any)?.parties?.find(
                        (party: any) => party.type === "petitioner",
                      )?.name || "-"}
                    </p>
                  </td>

                  <td className="px-4 py-3 text-gray-600 max-w-[120px]">
                    <p className="break-words">
                      {(result as any)?.parties?.find(
                        (party: any) => party.type === "respondent",
                      )?.name || "-"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    {renderStatusBadge(result)}
                  </td>
                  <td className="px-4 py-3 text-left">
                    {renderStatusButton(result)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Mobile Cards ── */}
        <div className="md:hidden space-y-3">
          {results.map((result, index) => (
            <div
              key={index}
              className={`bg-white border-l-4 rounded-xl p-4 shadow-sm hover:shadow-md transition ${
                result.streamingStatus === "live"
                  ? "border-l-red-500"
                  : result.streamingStatus === "available"
                    ? "border-l-blue-500"
                    : "border-l-gray-400"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar size={14} />
                  <span>{formatDisplayDate(result?.hearingDate)}</span>
                </div>
                {renderStatusBadge(result)}
              </div>

              <h4 className="font-semibold text-gray-900 mb-1">
                {result?.caseTitle}
              </h4>
              <p className="text-sm text-gray-600">
                Case: {result?.caseNumber}
              </p>
              <p className="text-sm text-gray-600">CNR: {result?.cnr}</p>
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                <Building size={13} />
                {result?.courtRoomName}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Petitioner:{" "}
                {(result as any)?.parties?.find(
                  (party: any) => party.type === "petitioner",
                )?.name || "-"}
              </p>

              <p className="text-sm text-gray-600">
                Respondent:{" "}
                {(result as any)?.parties?.find(
                  (party: any) => party.type === "respondent",
                )?.name || "-"}
              </p>

              <div className="mt-3">
                {renderStatusButton(result)}
              </div>
            </div>
          ))}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>

      {/* ── Archival Request Modal ── */}
      {showReqModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-xs">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">
              Request Access?
            </h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to request access to this archived
              recording?
            </p>
            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100"
                onClick={() => {
                  setShowReqModal(false);
                  setResult(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                onClick={() => result && handleRequest(result)}
                disabled={loading}
              >
                {loading ? "Requesting..." : "Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionResults;
